/**
 * ErrorBoundary — catches React render errors AND raw API-error strings
 * surfaced by hooks (e.g. `vault/documents 404`, `templates 404`).
 *
 * Renders a friendly Arabic/English message with a Retry button instead
 * of leaking implementation details to end users.
 *
 * Usage:
 *   <ErrorBoundary onRetry={() => reload()}>
 *     <YourPage />
 *   </ErrorBoundary>
 *
 * For non-React errors (raw error strings from API hooks), use the
 * functional `<FriendlyError>` component below directly.
 */
import React from 'react';
import { AlertCircle, RotateCcw } from 'lucide-react';

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary] caught', error, info);
  }

  handleRetry = () => {
    this.setState({ error: null });
    this.props.onRetry?.();
  };

  render() {
    if (this.state.error) {
      return (
        <FriendlyError
          isArabic={this.props.isArabic}
          onRetry={this.handleRetry}
        />
      );
    }
    return this.props.children;
  }
}

export function FriendlyError({ isArabic, onRetry, title, description }) {
  return (
    <div
      className="flex flex-col items-center justify-center text-center px-8 py-16 gap-4"
      data-testid="friendly-error"
    >
      <AlertCircle className="w-10 h-10 hv-text-muted" strokeWidth={1.5} />
      <div className="space-y-1">
        <h3 className="text-base hv-text">
          {title || (isArabic ? 'تعذر تحميل البيانات' : 'Could not load')}
        </h3>
        <p className="text-sm hv-text-muted max-w-sm">
          {description || (isArabic
            ? 'حدث خلل مؤقت أثناء التواصل مع الخادم. حاول مرة أخرى.'
            : 'A temporary issue occurred while reaching the server. Please retry.')}
        </p>
      </div>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm hv-border rounded hover:bg-black/5 transition"
          data-testid="friendly-error-retry"
        >
          <RotateCcw className="w-4 h-4" />
          {isArabic ? 'إعادة المحاولة' : 'Retry'}
        </button>
      )}
    </div>
  );
}

export default ErrorBoundary;
